/**
 * WHAT: Custom SQL Executor module -- main container with tabbed navigation.
 * WHY: Allows organizations to bring their own UAR SQL scripts into DAMA's
 *      automation pipeline, bridging existing processes with centralized
 *      database access governance.
 */

import React, { useState } from 'react';
import CustomDashboard from './CustomDashboard';
import SqlTemplates from './SqlTemplates';
import SqlJobs from './SqlJobs';
import SqlExecutions from './SqlExecutions';
import CustomSettings from './CustomSettings';

/**
 * WHAT: Main container component with tabbed navigation for the Custom SQL Executor module.
 * WHY: Serves as the entry point for organizations to execute their own UAR SQL scripts
 *      across multiple databases, consolidating templates, jobs, executions, and settings
 *      into a single navigable interface.
 */
const CustomSqlExecutor = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
    { id: 'templates', label: 'SQL Templates', icon: 'fa-file-code' },
    { id: 'jobs', label: 'Jobs', icon: 'fa-tasks' },
    { id: 'executions', label: 'Executions', icon: 'fa-play-circle' },
    { id: 'settings', label: 'Settings', icon: 'fa-cog' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <CustomDashboard onNavigate={setActiveTab} />;
      case 'templates':
        return <SqlTemplates />;
      case 'jobs':
        return <SqlJobs />;
      case 'executions':
        return <SqlExecutions />;
      case 'settings':
        return <CustomSettings />;
      default:
        return <CustomDashboard onNavigate={setActiveTab} />;
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-terminal me-2 text-primary"></i>
            Custom SQL Executor
          </h2>
          <p className="text-muted mb-0">
            Execute organization-defined SQL scripts across multiple databases for UAR data extraction
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        {tabs.map(tab => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon} me-2`}></i>
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Content Area */}
      <div className="tab-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default CustomSqlExecutor;
